import React, { useState, useRef } from "react";
import { Modal } from "@/components/ui/modal";
import { Wand2, Upload, Loader2, Sparkles, ImageIcon, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { generateImage, editImage } from "@/lib/ai";
import { cn } from "@/lib/utils";

// Reusable AuraMax AI Creative Studio: text->image + edit (upload/existing) with subject preservation.
// Props: open, onClose, kind ('post'|'avatar'|'banner'|'thumbnail'|'cover'|'brand'), title,
//        onUse(dataUrl), sourceImage (optional existing image to edit), aspect ('square'|'wide'|'portrait')

const KIND_IDEAS = {
  post: ["neon city rooftop at night", "moody film-grain portrait", "vibrant abstract energy waves"],
  avatar: ["bold studio-lit headshot, dramatic rim light", "cyber-glow creator portrait", "clean minimal profile shot"],
  banner: ["cinematic skyline at dusk, wide", "aura gradient light streaks, wide", "misty mountains at sunrise, wide"],
  thumbnail: ["shocked reaction, bold colors", "big text-space, high contrast", "dramatic spotlight on subject"],
  cover: ["dreamy album cover mood", "dark iconic minimal art", "glowing surreal landscape"],
  brand: ["signature aura color palette asset", "premium logo backdrop", "consistent brand hero visual"],
};

const ASPECT = { square: "aspect-square", wide: "aspect-[16/9]", portrait: "aspect-[3/4]" };

export function AiStudio({ open, onClose, kind = "post", title = "AI Creative Studio", onUse, sourceImage, aspect = "square" }) {
  const [tab, setTab] = useState(sourceImage ? "edit" : "create");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [ref, setRef] = useState(sourceImage || null);
  const fileRef = useRef(null);

  const reset = () => { setPrompt(""); setResult(null); setBusy(false); };
  const close = () => { onClose(); setTimeout(() => { reset(); setRef(sourceImage || null); setTab(sourceImage ? "edit" : "create"); }, 200); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setRef(reader.result); setTab("edit"); };
    reader.readAsDataURL(f);
  };

  const run = async () => {
    if (!prompt.trim()) { toast("Describe what you want", { description: "Give the studio a prompt to work with." }); return; }
    if (tab === "edit" && !ref) { toast("Add an image to edit first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { image } = tab === "edit" ? await editImage(prompt.trim(), ref, kind) : await generateImage(prompt.trim(), kind);
      setResult(image);
    } catch (e) {
      toast("Generation failed", { description: e.message });
    } finally { setBusy(false); }
  };

  const use = () => { if (result && onUse) { onUse(result); close(); } };

  return (
    <Modal open={open} onClose={close} testid="ai-studio" className="max-w-lg">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center gap-2.5 pr-8">
          <span className="grid h-9 w-9 place-items-center rounded-xl aura-gradient"><Wand2 className="h-5 w-5 text-black" strokeWidth={2.2} /></span>
          <div>
            <h2 className="font-display text-lg font-bold text-white">{title}</h2>
            <p className="text-xs text-am-text2">Powered by AuraMax AI · Nano Banana</p>
          </div>
        </div>

        {/* mode tabs */}
        <div className="mb-4 grid grid-cols-2 gap-2">
          <button onClick={() => setTab("create")} data-testid="studio-tab-create" className={cn("flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold", tab === "create" ? "border-transparent aura-gradient text-black" : "border-am-border bg-am-surface2 text-am-text2 hover:text-white")}>
            <Sparkles className="h-4 w-4" /> Generate
          </button>
          <button onClick={() => setTab("edit")} data-testid="studio-tab-edit" className={cn("flex items-center justify-center gap-2 rounded-xl border py-2.5 text-sm font-semibold", tab === "edit" ? "border-transparent aura-gradient text-black" : "border-am-border bg-am-surface2 text-am-text2 hover:text-white")}>
            <ImageIcon className="h-4 w-4" /> Edit image
          </button>
        </div>

        {/* edit source */}
        {tab === "edit" && (
          <div className="mb-4">
            {ref ? (
              <div className="relative overflow-hidden rounded-xl border border-am-border">
                <img src={ref} alt="source" className="max-h-48 w-full object-cover" />
                <button onClick={() => fileRef.current?.click()} className="absolute bottom-2 right-2 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur"><RefreshCw className="h-3 w-3" /> Replace</button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} data-testid="studio-upload" className="flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-am-border-strong bg-am-surface2 py-8 text-am-text2 hover:text-white">
                <Upload className="h-5 w-5" /> <span className="text-sm font-semibold">Upload an image to edit</span>
                <span className="text-xs text-am-text3">Your subject is preserved during edits</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
          </div>
        )}

        {/* prompt */}
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          data-testid="studio-prompt"
          rows={2}
          placeholder={tab === "edit" ? "Describe the change (e.g. 'add a neon aura glow behind me')" : "Describe the image you want to create…"}
          className="w-full resize-none rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm text-white placeholder:text-am-text3 outline-none focus:border-am-border-strong"
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(KIND_IDEAS[kind] || KIND_IDEAS.post).map((idea) => (
            <button key={idea} onClick={() => setPrompt(idea)} className="rounded-full border border-am-border bg-am-surface2 px-3 py-1 text-xs text-am-text2 hover:text-white">{idea}</button>
          ))}
        </div>

        {/* result */}
        {(busy || result) && (
          <div className={cn("mt-4 overflow-hidden rounded-2xl border border-am-border bg-am-surface2", ASPECT[aspect])} data-testid="studio-result">
            {busy ? (
              <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-2 text-am-text2">
                <Loader2 className="h-6 w-6 animate-spin aura-text" />
                <span className="text-sm">Creating your visual…</span>
              </div>
            ) : (
              <img src={result} alt="result" className="h-full w-full object-cover" />
            )}
          </div>
        )}

        {/* actions */}
        <div className="mt-5 flex gap-2.5">
          <button onClick={run} disabled={busy} data-testid="studio-generate" className="flex flex-1 items-center justify-center gap-2 rounded-full p-[1.5px] aura-gradient text-sm font-bold disabled:opacity-60">
            <span className="flex w-full items-center justify-center gap-2 rounded-full bg-am-bg py-3 text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4 aura-text" />}
              {result ? "Regenerate" : (tab === "edit" ? "Edit with AI" : "Generate")}
            </span>
          </button>
          {result && (
            <button onClick={use} data-testid="studio-use" className="flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-black transition-transform active:scale-95">
              <Check className="h-4 w-4" /> Use
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
